import React, {useState} from "react";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {Copy, Link2} from "lucide-react";

import {
    BulkInviteClaimRow, BulkInviteRow, createBulkInvite, listBulkInviteClaims,
    listBulkInvites, revokeBulkInvite,
} from "../../api/bulk_invite_api";
import {APP_URL} from "../../api/apiBase";
import {Button} from "../ui/button";
import {Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger} from "../ui/dialog";
import {Input} from "../ui/input";
import {Label} from "../ui/label";
import {useToast} from "../ui/use-toast";

function make_invite_url(token: string) {
    return `${APP_URL}/invite/${token}`;
}

/**
 * Developer side of the bulk invite: make one link, paste it into your own
 * mass email. Nobody is pre-registered - see who actually used it here.
 */
export function BulkInviteDialog() {
    const {project_id} = useParams();
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();

    const [invites, setInvites] = useState<BulkInviteRow[]>([]);
    const [label, setLabel] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);
    const [claims, setClaims] = useState<BulkInviteClaimRow[] | null>(null);
    const [openToken, setOpenToken] = useState<string | null>(null);

    const load = () => {
        if (!accessToken || !project_id) return;
        listBulkInvites(project_id, accessToken)
            .then(setInvites)
            .catch(() => setInvites([]));
    };

    const reset = (open: boolean) => {
        setLabel('');
        setClaims(null);
        setOpenToken(null);
        if (open) load();
    };

    const copy = (token: string) => {
        const url = make_invite_url(token);
        navigator.clipboard.writeText(url).then(() => {
            toast({title: 'Link copied to clipboard', description: url});
        }).catch(() => {
            toast({title: 'Error', description: 'Failed to copy link', variant: 'destructive'});
        });
    };

    const handleCreate = () => {
        if (!accessToken || !project_id) return;
        setBusy(true);
        createBulkInvite(project_id, label || null, accessToken)
            .then((data) => {
                setBusy(false);
                setLabel('');
                copy(data.token);
                load();
            })
            .catch((e) => {
                setBusy(false);
                toast({
                    title: 'Error',
                    description: e.message || 'Could not create link',
                    variant: 'destructive',
                });
            });
    };

    const handleRevoke = (token: string) => {
        if (!accessToken || !project_id) return;
        revokeBulkInvite(project_id, token, accessToken)
            .then(() => load())
            .catch((e) => toast({
                title: 'Error',
                description: e.message || 'Could not revoke link',
                variant: 'destructive',
            }));
    };

    const showClaims = (token: string) => {
        if (!accessToken || !project_id) return;
        if (openToken === token) {
            setOpenToken(null);
            setClaims(null);
            return;
        }
        setOpenToken(token);
        listBulkInviteClaims(project_id, token, accessToken)
            .then(setClaims)
            .catch(() => setClaims([]));
    };

    return <Dialog onOpenChange={reset}>
        <DialogTrigger asChild>
            <Button variant="outline" className="mr-2">
                <Link2 className="h-4 w-4 mr-2"/>Bulk Invite
            </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
            <DialogTitle className="font-normal !text-[17px] mb-2">Bulk invite link</DialogTitle>

            <p className="text-sm text-gray-600">
                One link you can paste into your own email to any number of bidders.
                Whoever opens it signs up as a builder and picks their scopes.
            </p>

            <div className="space-y-2">
                <Label>Name this link (optional)</Label>
                <Input
                    placeholder="e.g. Spring 2026 bid list"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
                <Button onClick={handleCreate} disabled={busy}>
                    {busy ? 'Creating...' : 'Create link'}
                </Button>
            </div>

            {invites.length > 0 && <div className="space-y-2 max-h-72 overflow-y-auto">
                {invites.map((inv) => (
                    <div key={inv._id} className="rounded-md border p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className={inv.revoked ? 'line-through text-gray-400' : ''}>
                                    {inv.label || 'Untitled link'}
                                </div>
                                <button
                                    className="text-xs text-gray-500 underline"
                                    onClick={() => showClaims(inv.token)}
                                >
                                    {inv.num_claims} joined
                                </button>
                            </div>
                            {!inv.revoked && <>
                                <Button variant="ghost" size="sm" onClick={() => copy(inv.token)}>
                                    <Copy className="h-4 w-4"/>
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleRevoke(inv.token)}>
                                    Revoke
                                </Button>
                            </>}
                        </div>

                        {openToken === inv.token && claims !== null && <div className="mt-2 border-t pt-2">
                            {claims.length === 0
                                ? <div className="text-xs text-gray-500">Nobody has used this link yet.</div>
                                : claims.map((c) => (
                                    <div key={c._id} className="text-xs flex justify-between py-0.5">
                                        <span>{c.user_email}</span>
                                        <span className="text-gray-500">{c.company_name}</span>
                                    </div>
                                ))}
                        </div>}
                    </div>
                ))}
            </div>}

            <DialogFooter className="sm:justify-start"/>
        </DialogContent>
    </Dialog>;
}

export default BulkInviteDialog;
